import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
    MapColorsAlt,
} from 'src/app/shared/components/map/map.constants';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorBlack,
} from 'src/app/shared/all.constants';
import { LegendConfig } from 'src/app/shared/components/map/map.component';
import { PathOptions } from 'leaflet';
import { convertNumber } from 'src/app/shared/all.helpers';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    ENEDIS_CUSTOMERS_CLAIM_EVOL_COMLPLAINTS_CHART,
    ENEDIS_CUSTOMERS_CLAIM_EVOL_RATE_RESPONSE_CHART,
    ENEDIS_CUSTOMERS_CLAIM_EVOL_RATE_USERS_CHART,
    ENEDIS_CUSTOMERS_CLAIM_KPIS,
    ENEDIS_CUSTOMERS_CLAIM_RATE_MAP,
    ENEDIS_CUSTOMERS_CLAIMS_COMPARISON_CHART,
} from '../../assets/keys';

const KEYS = [
    ENEDIS_CUSTOMERS_CLAIM_RATE_MAP,
    ENEDIS_CUSTOMERS_CLAIM_KPIS,
    ENEDIS_CUSTOMERS_CLAIMS_COMPARISON_CHART,
    ENEDIS_CUSTOMERS_CLAIM_EVOL_COMLPLAINTS_CHART,
    ENEDIS_CUSTOMERS_CLAIM_EVOL_RATE_RESPONSE_CHART,
    ENEDIS_CUSTOMERS_CLAIM_EVOL_RATE_USERS_CHART,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    volumeComplaints?: number;
    volumeComplaintsEvolution?: number;

    responseComplaintsRate?: number;
    responseComplaintsRateEvolution?: number;
}

interface ComparisonChartData {
    id?: number;
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
    error?: boolean;
}

interface ComplChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    connection?: number;
    reception?: number;
    readingInvoicing?: number;
    technicalIntervention?: number;
    qualitySupply?: number;
    linkyInstallation?: number;
    total?: number;
    error?: boolean;
}

interface RateChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    rateComplaints?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-enedis-claims',
    templateUrl: './enedis-claims.component.html',
    styleUrls: ['./enedis-claims.component.scss'],
})
export class EnedisClaimsComponent extends GenericComponent {
    public texts = CC_CONSTANTS.enedisClaims.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_CLAIM_KPIS),
    ) as Signal<KpiData[]>;

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_CLAIM_RATE_MAP),
    ) as Signal<any[]>;

    public rateClaimsComparisonChartDataSignal: Signal<ComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_CLAIMS_COMPARISON_CHART),
        ) as Signal<ComparisonChartData[]>;
    public rateClaimsComparisonChartSeries: ApexAxisChartSeries = [];
    public rateClaimsComparisonChartDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public rateClaimsComparisonCustomTooltip: ApexTooltip['custom'];
    public rateClaimsComparisonChartError: boolean = true;
    public comparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(1),
            borderRadius: 1,
        },
    };

    public rateClaimsComparisonChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 30,
            max: 90,
        },
    ];

    public comparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };

    public legendConfig: LegendConfig[] = [
        {
            title: 'Taux de de réclamations traitées par Enedis pour 10 000 clients aux TRV',
            values: [
                { color: MapColorsAlt.darkGreen, value: 'Moins de 50 réclamations pour 10 000 us' },
                {
                    color: MapColorsAlt.lightGreen,
                    value: 'Entre 50 et 60 réclamations pour 10 000 us',
                },
                {
                    color: MapColorsAlt.lighterGreen,
                    value: 'Entre 60 et 70 réclamations pour 10 000 us',
                },
                {
                    color: MapColorsAlt.paleGreen,
                    value: 'Entre 70 et 80 réclamations pour 10 000 us',
                },
                { color: colorAec4Orange, value: 'Entre 80 et 100 réclamations pour 10 000 us' },
                { color: colorAec5Brown, value: 'Entre 100 et 120 réclamations pour 10 000 us' },
                { color: colorAec8RedDark, value: 'Plus de 120 réclamations pour 10 000 us' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];

    public complChartError: boolean = true;
    public complChartSeries: ApexAxisChartSeries = [];
    public complDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.evolComplaintsEnedisChart.totalTitle),
    };
    public complXaxisCategories: ApexXAxis['categories'];
    public complYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolComplaintsEnedisChart.connectionAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolComplaintsEnedisChart.yaxisTitleLeft,
            },
        },
    ];
    public complChartData: Signal<ComplChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_CLAIM_EVOL_COMLPLAINTS_CHART),
    ) as Signal<ComplChartData[]>;
    public complLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec5Brown,
                colorAec2BlueDark,
                colorAec3Green,
                colorAec4Orange,
                colorAec1BlueLight,
                colorAec6Gray,
                colorBlack,
            ],
        },
    };
    public evolComplaintsCustomTooltip: ApexTooltip['custom'];

    public plotOptions: ApexPlotOptions = {
        bar: {
            horizontal: false,
            columnWidth: '30%',
        },
    };

    public respChartError: boolean = true;
    public respChartSeries: ApexAxisChartSeries = [];
    public respDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null }),
    };
    public respXaxisCategories: ApexXAxis['categories'];
    public respYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolRateResponseComplaintsChart.rateComplaintsAxisTitle,
            min: 50,
            title: {
                text: this.texts.evolRateResponseComplaintsChart.yaxisTitleLeft,
            },
        },
    ];
    public respChartData: Signal<RateChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_CLAIM_EVOL_RATE_RESPONSE_CHART),
    ) as Signal<RateChartData[]>;

    public userChartError: boolean = true;
    public userChartSeries: ApexAxisChartSeries = [];
    public userDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: 0 }),
    };
    public userXaxisCategories: ApexXAxis['categories'];
    public userYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolRateComplaintsUserChart.rateComplaintsAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolRateComplaintsUserChart.yaxisTitleLeft,
            },
        },
    ];
    public userChartData: Signal<RateChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_CLAIM_EVOL_RATE_USERS_CHART),
    ) as Signal<RateChartData[]>;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const rateClaimsComparisonChartData = this.rateClaimsComparisonChartDataSignal();
            this.rateClaimsComparisonChartError = this.checkForError(
                rateClaimsComparisonChartData,
                ['category', 'min', 'max', 'mean'],
            );
            if (!this.rateClaimsComparisonChartError) {
                this.rateClaimsComparisonChartSeries = [
                    {
                        name: this.texts.rateClaimsComparisonChart.serieLabel,
                        data: rateClaimsComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.rateClaimsComparisonChart.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },

                                {
                                    name: this.texts.rateClaimsComparisonChart.clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.rateClaimsComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.rateClaimsComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }

            const complDataList = this.complChartData();
            this.complChartError = this.checkForError(complDataList, [
                'xYear',
                'connection',
                'reception',
                'readingInvoicing',
                'technicalIntervention',
                'qualitySupply',
                'linkyInstallation',
            ]);

            if (!this.complChartError) {
                this.complXaxisCategories = complDataList.map((data) => data.xYear);
                const totales = this.getTotales(complDataList, [
                    'connection',
                    'reception',
                    'readingInvoicing',
                    'technicalIntervention',
                    'qualitySupply',
                    'linkyInstallation',
                ]);
                this.complChartSeries = [
                    {
                        name: this.texts.evolComplaintsEnedisChart.connectionAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.connection!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.evolComplaintsEnedisChart.receptionAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.reception!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.evolComplaintsEnedisChart.readingInvoicingAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.readingInvoicing!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolComplaintsEnedisChart.technicalInterventionAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.technicalIntervention!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.evolComplaintsEnedisChart.qualitySupplyAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.qualitySupply!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.evolComplaintsEnedisChart.linkyInstallationAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.linkyInstallation!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.evolComplaintsEnedisChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.complDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5, 6];
            }

            const respDataList = this.respChartData();
            this.respChartError = this.checkForError(respDataList, ['xYear', 'rateComplaints']);

            if (!this.respChartError) {
                this.respXaxisCategories = respDataList.map((data) => data.xYear);
                this.respChartSeries = [
                    {
                        name: this.texts.evolRateResponseComplaintsChart.rateComplaintsAxisTitle,
                        type: 'line',
                        data: respDataList.map((data) => data.rateComplaints!),
                        color: colorAec7RedLight,
                    },
                ];

                this.respDataLabels.enabledOnSeries = [0];
            }

            const userDataList = this.userChartData();
            this.userChartError = this.checkForError(userDataList, ['xYear', 'rateComplaints']);

            if (!this.userChartError) {
                this.userXaxisCategories = userDataList.map((data) => data.xYear);
                this.userChartSeries = [
                    {
                        name: this.texts.evolRateComplaintsUserChart.rateComplaintsAxisTitle,
                        type: 'line',
                        data: userDataList.map((data) => data.rateComplaints!),
                        color: colorAec2BlueDark,
                    },
                ];

                this.userDataLabels.enabledOnSeries = [0];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.evolComplaintsCustomTooltip = this.setEvolComplaintsCustomTooltip();

        this.rateClaimsComparisonCustomTooltip = this.setChartCustomTooltip(
            this.texts.rateClaimsComparisonChart.precision,
        );
    }

    public setEvolComplaintsCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolComplaintsEnedisChart.precision;
            const connectionSerie = w.globals.initialSeries[0];
            const {
                name: connectionName,
                color: connectionColor,
                valueStringified: connectionStringified,
            } = this.setCustomTooltipData(precision, connectionSerie, dataPointIndex);

            const receptionSerie = w.globals.initialSeries[1];
            const {
                name: receptionName,
                color: receptionColor,
                valueStringified: receptionStringified,
            } = this.setCustomTooltipData(precision, receptionSerie, dataPointIndex);

            const readingInvoicingSerie = w.globals.initialSeries[2];
            const {
                name: readingInvoicingName,
                color: readingInvoicingColor,
                valueStringified: readingInvoicingStringified,
            } = this.setCustomTooltipData(precision, readingInvoicingSerie, dataPointIndex);

            const technicalInterventionSerie = w.globals.initialSeries[3];
            const {
                name: technicalInterventionName,
                color: technicalInterventionColor,
                valueStringified: technicalInterventionStringified,
            } = this.setCustomTooltipData(precision, technicalInterventionSerie, dataPointIndex);

            const qualitySupplySerie = w.globals.initialSeries[4];
            const {
                name: qualitySupplyName,
                color: qualitySupplyColor,
                valueStringified: qualitySupplyStringified,
            } = this.setCustomTooltipData(precision, qualitySupplySerie, dataPointIndex);

            const linkyInstallationSerie = w.globals.initialSeries[5];
            const {
                name: linkyInstallationName,
                color: linkyInstallationColor,
                valueStringified: linkyInstallationStringified,
            } = this.setCustomTooltipData(precision, linkyInstallationSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[6];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: connectionColor,
                    name: connectionName,
                    value: connectionStringified,
                },
                {
                    color: receptionColor,
                    name: receptionName,
                    value: receptionStringified,
                },
                {
                    color: readingInvoicingColor,
                    name: readingInvoicingName,
                    value: readingInvoicingStringified,
                },
                {
                    color: technicalInterventionColor,
                    name: technicalInterventionName,
                    value: technicalInterventionStringified,
                },
                {
                    color: qualitySupplyColor,
                    name: qualitySupplyName,
                    value: qualitySupplyStringified,
                },
                {
                    color: linkyInstallationColor,
                    name: linkyInstallationName,
                    value: linkyInstallationStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setChartCustomTooltip(precision: number | null = null): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const dataSerie = w.globals.initialSeries[seriesIndex];
            const serieName = dataSerie.name;
            const serieColor = dataSerie.color;
            const dataPoint = dataSerie.data[dataPointIndex];
            const min = convertNumber(dataPoint.y[0], precision, false);
            const max = convertNumber(dataPoint.y[1], precision, false);

            const meanGoal = dataPoint.goals[0];
            const meanValue = convertNumber(meanGoal.value, precision, false);
            const meanName = meanGoal.name;
            const meanColor = meanGoal.strokeColor;

            const clientValueGoal = dataPoint.goals[1];
            const clientValue = convertNumber(clientValueGoal.value, precision, false);
            const clientValueName = clientValueGoal.name;
            const clientColor = clientValueGoal.strokeColor;

            return `
                <div class="apexcharts-tooltip-title">
                    ${dataPoint.x}
                </div>

                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} récla./10 000 us.</span>
                        </div>
                         <div class="apexcharts-tooltip-goals-group">
                         </div>
                    </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${meanValue} récla./10 000 us.</span>
                        </div>
                         <div class="apexcharts-tooltip-goals-group">
                         </div>
                    </div>
                </div>
                 <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${clientValue} récla./10 000 us.</span>
                        </div>
                    </div>
                </div>
            `;
        };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        if (!feature?.properties || feature.properties['enedisClaimRate'] === null) {
            return EmptyPolygonStyle;
        }

        // Cities style
        let fillColor = MapColorsAlt.darkGreen;
        const enedisClaimRate = Number(feature.properties['enedisClaimRate']);
        if (enedisClaimRate > 120) {
            fillColor = colorAec8RedDark;
        } else if (enedisClaimRate > 100) {
            fillColor = colorAec5Brown;
        } else if (enedisClaimRate > 80) {
            fillColor = colorAec4Orange;
        } else if (enedisClaimRate > 70) {
            fillColor = MapColorsAlt.paleGreen;
        } else if (enedisClaimRate > 60) {
            fillColor = MapColorsAlt.lighterGreen;
        } else if (enedisClaimRate > 50) {
            fillColor = MapColorsAlt.lightGreen;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        const { cityName, enedisClaimRate, isInConcession } = feature.properties;
        if (!isInConcession) {
            return `${cityName} - Hors concession`;
        }

        const valueStringified = convertNumber(Number(enedisClaimRate), null);
        return `${cityName} - ${valueStringified} réclamations d'Enedis pour 10 000 usagers`;
    }
}
